// Narrative card for the guided first-run loop (DEMO view only). A lower-third,
// centered glass banner the storyDirector raises to guide the visitor through the
// loop ("The shop is dark. Start the generator.") — VO-ready text now, audio
// later. DISTINCT from demoChip: demoChip is a tiny transient label that flashes
// at the cursor on a hero click; this is a deliberate, legible instruction that
// can PERSIST until the gated action is taken (the watchdog escalates to it), or
// auto-dismiss for a passing beat. One card at a time; re-showing swaps the text
// in place rather than stacking.

const FADE_MS = 420;
const DEFAULT_HOLD_MS = 5200;

export interface StoryCard {
  /** Raise the card with `text`. `persist:true` keeps it up until hide() (gated
   *  guidance the visitor must act on); otherwise it auto-fades after `holdMs`.
   *  Calling show() again swaps the text without a flicker and resets the timer. */
  show(text: string, opts?: { persist?: boolean; holdMs?: number }): void;
  hide(): void;
  /** Currently raised? (the watchdog avoids re-raising an already-shown card.) */
  readonly visible: boolean;
  dispose(): void;
}

export function createStoryCard(): StoryCard {
  const card = document.createElement('div');
  card.id = 'lec-story-card';
  card.style.cssText = [
    'position: fixed',
    'bottom: 24%',                 // lower third — clear of the top chrome + the 3D focal point
    'left: 50%',
    'transform: translate(-50%, 8px)',
    'max-width: min(460px, 78vw)',
    'padding: 13px 22px',
    'text-align: center',
    // LIGHT warm-paper card (John, 2026-06-29: "make the text cards a light style
    // so they contrast with the buttons better"). The action pills are dark glass;
    // the guidance card is now light + dark-inked, so narration and action read as
    // two distinct surfaces rather than one blurred glass family. Still more solid
    // + larger than demoChip so it reads as deliberate narration, not a flash.
    'background: rgba(247, 244, 238, 0.93)',
    'border: 1px solid rgba(58, 70, 92, 0.22)',
    'border-radius: 14px',
    'color: #232a36',
    'font: 500 15px/1.42 system-ui, sans-serif',
    'letter-spacing: 0.01em',
    'text-wrap: balance',
    'backdrop-filter: blur(8px)',
    '-webkit-backdrop-filter: blur(8px)',
    'box-shadow: 0 6px 26px rgba(0, 0, 0, 0.38)',
    'pointer-events: none',        // guidance, never blocks the scene
    'user-select: none',
    'z-index: 1150',               // above demoTimeline (1000), below demoChip (1200)
    'opacity: 0',
    `transition: opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
  ].join('; ');
  document.body.appendChild(card);

  let hideTimer = 0;
  let shown = false;

  const show = (text: string, opts?: { persist?: boolean; holdMs?: number }): void => {
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = 0; }
    card.textContent = text;
    card.style.opacity = '1';
    card.style.transform = 'translate(-50%, 0)';
    shown = true;
    if (!opts?.persist) {
      hideTimer = window.setTimeout(hide, opts?.holdMs ?? DEFAULT_HOLD_MS);
    }
  };

  const hide = (): void => {
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = 0; }
    if (!shown) return;
    card.style.opacity = '0';
    card.style.transform = 'translate(-50%, 8px)';
    shown = false;
  };

  return {
    show,
    hide,
    get visible() { return shown; },
    dispose(): void {
      if (hideTimer) clearTimeout(hideTimer);
      card.remove();
    },
  };
}
